<script lang="ts">
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import {onDestroy, onMount} from 'svelte';
    import {getInjector} from 'core/svelte-context/context';
    import {BarcodeScannerService} from 'shared/barcode-scanner/barcode-scanner.service';
    import type {Subscription} from 'rxjs';
    import {unsubscribeAllSubscriptions} from 'shared/utils/observables-utils';
    import {ScannerState} from 'shared/barcode-scanner/barcode-scanner.types';

    const barcodeScannerService = getInjector().getByClass(BarcodeScannerService);

    // async component init:
    let isRegistered = false; // true after full viewer and scanner initialization
    let isDestroyed = false; // true if component is destroyed

    let isViewerClosed = true;
    let hideScannerVideo = true;
    let showLoadingIndicator = false;

    let videoElementRef: HTMLVideoElement;
    let openStateSubscription: Subscription;
    let scannerStateSubscription: Subscription;

    onMount(async () => {
        if (!await barcodeScannerService.isUsable()) {
            return;
        }

        if (isDestroyed) {
            return;
        }

        const openState$ = await barcodeScannerService.registerScannerViewer(videoElementRef);
        openStateSubscription = openState$.subscribe((isOpen) => isViewerClosed = !isOpen);
        scannerStateSubscription = barcodeScannerService.getScannerState$().subscribe(processScannerStateChange);
        isRegistered = true;
    });

    onDestroy(() => {
        isDestroyed = true;

        if (!isRegistered) {
            return;
        }

        unsubscribeAllSubscriptions(openStateSubscription, scannerStateSubscription);
        barcodeScannerService.unregisterScannerViewer();
        isRegistered = false;
    });

    const processScannerStateChange = (state: ScannerState) => {
        switch (state) {
            case ScannerState.STARTING:
                hideScannerVideo = true;
                showLoadingIndicator = true;
                break;
            case ScannerState.OPERATING:
                hideScannerVideo = false;
                showLoadingIndicator = false;
                break;
            default:
                hideScannerVideo = true;
                showLoadingIndicator = false;
        }
    }
</script>

<div class="barcode-scanner-video-container" class:hidden={isViewerClosed}>
    <KpLoadingBlock showWhen="{showLoadingIndicator}"/>

    <div class="scanner-animation-container" class:hidden={hideScannerVideo}>
        <div class="scanner-animation"></div>
    </div>

    <video id="barcode-scanner-video" muted bind:this={videoElementRef} class:hidden={hideScannerVideo}></video>
</div>

<style lang="less">
    .barcode-scanner-video-container {
        z-index: 2000; // more than bootstrap sticky
        position: fixed;
        background: white;

        width: 640px;
        max-width: 100%;

        height: auto;
        max-height: 50%;

        bottom: 0;
        left: 0;
        right: 0;

        margin: auto;

        video {
            max-width: inherit;
            position: relative;
            bottom: 0;
            display: block;
            margin-left: auto;
            margin-right: auto;
        }

        :global(> *) { // all direct descendants of barcode-scanner-video-container
            border: 1px solid gray;
        }
    }

    .scanner-animation-container {
        max-width: inherit;
        display: block;
        margin-left: auto;
        margin-right: auto;
        bottom: 0;
        top: 0;
        width: inherit;
        position: absolute;
        background-color: transparent;
    }

    .scanner-animation {
        z-index: 2001;
        width: 80%;
        left: 10%;
        right: 10%;
        height: 0.83333333333%;
        background-color: red;
        opacity: 0.7;
        position: relative;
        box-shadow: 0 0 8px 10px rgba(170, 11, 23, 0.49);
        top: 50%;

        animation: scan-anim-frames 4s linear infinite;
        will-change: transform;
    }

    @keyframes scan-anim-frames {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(+4200%);
        }
        75% {
            transform: translateY(-4200%);
        }
    }
</style>